/*
 * File: src/components/OutstandingOrders/OutstandingOrderShippingStatus.jsx
 * Purpose: Displays the shipped / partial / ready-to-invoice chips and shipment details.
 */

import React from 'react';
import PropTypes from 'prop-types';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Snackbar from '@mui/material/Snackbar';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import TableDate from '../TableDate.jsx';

function OutstandingOrderShippingStatus({ shipments = [], summary }) {
  const [notice, setNotice] = React.useState(null);
  const trackingNumbers = [
    ...new Set(
      shipments.map((shipment) => String(shipment.Tracking_No ?? '').trim()).filter(Boolean)
    ),
  ];

  const copyTracking = async (event, numbers) => {
    event.preventDefault();
    event.stopPropagation();
    if (!numbers.length) {
      setNotice({ severity: 'info', message: 'No tracking number available.' });
      return;
    }
    try {
      if (!navigator.clipboard?.writeText) {
        throw new Error('Clipboard unavailable');
      }
      await navigator.clipboard.writeText(numbers.join('\n'));
      setNotice({
        severity: 'success',
        message: numbers.length === 1 ? 'Tracking number copied' : 'Tracking numbers copied',
      });
    } catch {
      setNotice({
        severity: 'error',
        message: 'Could not copy. Select and copy the tracking number manually.',
      });
    }
  };

  if (!summary) return <span>—</span>;
  if (summary.status === 'unreceived') return <span>—</span>;
  const isPartial = summary.status === 'partial';
  const isReady = summary.status === 'complete' && summary.readyToInvoice;

  const details = (
    <Box sx={{ maxHeight: 320, overflow: 'auto' }}>
      <Typography variant="body2" sx={{ px: 2, pt: 1 }}>
        Received: {summary.receivedQty} / Ordered: {summary.orderedQty ?? 'Unavailable'}
      </Typography>
      <Table size="small" aria-label="Shipping tracking information">
        <TableHead>
          <TableRow>
            <TableCell>Date received</TableCell>
            <TableCell>Tracking number</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {shipments.map((shipment) => {
            const tracking = String(shipment.Tracking_No ?? '').trim();
            return (
              <TableRow key={shipment.ID}>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {shipment.date_received ? (
                    <TableDate date={shipment.date_received} />
                  ) : (
                    'Not provided'
                  )}
                </TableCell>
                <TableCell sx={{ overflowWrap: 'anywhere', userSelect: 'text' }}>
                  {tracking ? (
                    <Box
                      component="button"
                      type="button"
                      aria-label={`Copy tracking number ${tracking}`}
                      onClick={(event) => copyTracking(event, [tracking])}
                      onContextMenu={(event) => copyTracking(event, [tracking])}
                      sx={{
                        border: 0,
                        p: 0,
                        background: 'none',
                        color: 'primary.main',
                        font: 'inherit',
                        textAlign: 'left',
                        cursor: 'copy',
                        overflowWrap: 'anywhere',
                        userSelect: 'text',
                      }}
                    >
                      {tracking}
                    </Box>
                  ) : (
                    'Not provided'
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      <Typography variant="caption" component="p" sx={{ px: 2, py: 1 }}>
        Click or right-click a tracking number to copy it. Right-click the shipping indicator to
        copy all numbers.
      </Typography>
    </Box>
  );

  return (
    <>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
        <Tooltip
          title={details}
          arrow
          placement="top"
          leaveDelay={200}
          slotProps={{
            tooltip: {
              sx: {
                bgcolor: 'background.paper',
                color: 'text.primary',
                boxShadow: 4,
                border: 1,
                borderColor: 'divider',
                maxWidth: 460,
                p: 0,
              },
            },
          }}
        >
          <Chip
            label={isPartial ? 'Partial shipped' : 'Shipped'}
            color={isPartial ? 'warning' : 'success'}
            size="small"
            tabIndex={0}
            onContextMenu={(event) => copyTracking(event, trackingNumbers)}
            data-cy="outstanding-order-shipped"
          />
        </Tooltip>
        {isReady && (
          <Chip
            label="Ready to invoice"
            color="success"
            size="small"
            data-cy="outstanding-order-ready-to-invoice"
          />
        )}
      </Stack>
      <Snackbar open={Boolean(notice)} autoHideDuration={3500} onClose={() => setNotice(null)}>
        <Alert severity={notice?.severity || 'success'} onClose={() => setNotice(null)}>
          {notice?.message}
        </Alert>
      </Snackbar>
    </>
  );
}

OutstandingOrderShippingStatus.propTypes = {
  summary: PropTypes.shape({
    orderedQty: PropTypes.number,
    receivedQty: PropTypes.number.isRequired,
    readyToInvoice: PropTypes.bool.isRequired,
    status: PropTypes.oneOf(['unreceived', 'partial', 'complete', 'unknown']).isRequired,
  }),
  shipments: PropTypes.arrayOf(
    PropTypes.shape({
      ID: PropTypes.string.isRequired,
      date_received: PropTypes.string,
      Tracking_No: PropTypes.string,
    })
  ),
};

export default React.memo(OutstandingOrderShippingStatus);
